import { useTranslation } from 'react-i18next';
import {
    useAppSelector,
    useTheme,
    Skeleton,
    Typography,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    ChartTooltip,
    ResponsiveContainer,
    LabelList,
    Box,
} from '../../../../../base';
import { Directions } from '../../../../../common/consts/system.consts';
import { reportsSelectors } from '../../../../../actions';
import { StyledConversionCard } from '../StageConversionRates.styles';
import { getConversionSteps, isJobView } from '../../utils/funnelDataUtils';

interface ICustomBarShapeProps {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    payload?: { value: number; total: number };
    fill?: string;
}

interface ITooltipProps {
    active?: boolean;
    payload?: Array<{ payload: { value: number; total: number } }>;
}

interface ILabelListContentProps {
    y?: number;
    height?: number;
    value?: string | number;
}

const ConversionBarChart = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const funnelData = useAppSelector(reportsSelectors.getFunnelData);
    const isLoading = useAppSelector(reportsSelectors.getIsLoadingFunnel);

    const isRtl = theme.direction === Directions.rtl;
    const conversionSteps = getConversionSteps(funnelData);

    const conversionData = conversionSteps.map((step) => ({
        name: `${step.fromLabelKey ? t(step.fromLabelKey) : step.fromLabel} ${isRtl ? '←' : '→'} ${step.toLabelKey ? t(step.toLabelKey) : step.toLabel}`,
        value: step.toCount,
        total: step.fromCount,
        percentageLabel: `${step.percentage.toFixed(1)}%`,
    }));

    // Dynamic height based on number of conversions
    const chartHeight = Math.max(120, conversionData.length * 50);

    if (isLoading) {
        return (
            <Skeleton
                variant="rectangular"
                width="100%"
                height={250}
                sx={{ borderRadius: 1 }}
            />
        );
    }

    const CustomBarShape = (props: ICustomBarShapeProps) => {
        const { x = 0, y = 0, width = 0, height = 0, payload } = props;
        if (!payload) return null;

        const trackWidth =
            payload.value > 0 ? (payload.total / payload.value) * width : 0;

        const trackX = Math.min(x, x + trackWidth);
        const barX = Math.min(x, x + width);
        return (
            <g>
                <rect
                    x={trackX}
                    y={y}
                    width={Math.abs(trackWidth)}
                    height={height}
                    rx={6}
                    ry={6}
                    fill={theme.palette.warning.main}
                    opacity={0.2}
                />
                <rect
                    x={barX}
                    y={y}
                    width={Math.abs(width)}
                    height={height}
                    rx={6}
                    ry={6}
                    fill={theme.palette.warning.main}
                />
            </g>
        );
    };

    const renderTooltipContent = ({ active, payload }: ITooltipProps) => {
        if (!active || !payload?.length) return null;
        const { value, total } = payload[0].payload;

        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.grey[800],
                    color: theme.palette.common.white,
                    padding: theme.spacing(0.5, 1.5),
                    borderRadius: 1.5,
                    ...theme.typography.caption,
                }}
            >
                {t('reportsPage.stageConversionRates.outOf', {
                    count: value,
                    total,
                })}
            </Box>
        );
    };

    const getSubtitle = () => {
        if (isJobView(funnelData)) {
            return t('reportsPage.stageConversionRates.barChartJobViewSubtitle');
        }
        return t('reportsPage.stageConversionRates.barChartSubtitle');
    };

    return (
        <StyledConversionCard>
            <Typography variant="h6">
                {t('reportsPage.stageConversionRates.title')}
            </Typography>
            <Typography variant="body1" color={theme.palette.grey[500]}>
                {getSubtitle()}
            </Typography>
            <Box
                sx={{
                    maxHeight: conversionData.length > 4 ? 300 : 'auto',
                    overflowY: conversionData.length > 4 ? 'auto' : 'visible',
                }}
            >
                <ResponsiveContainer  height={chartHeight}>
                    <BarChart
                        data={conversionData}
                        layout="vertical"
                        margin={{
                            top: 5,
                            right: isRtl ? 5 : 40,
                            left: isRtl ? 40 : 5,
                            bottom: 5,
                        }}
                    >
                        <XAxis
                            type="number"
                            domain={[0, funnelData?.total ?? 0]}
                            reversed={isRtl}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            orientation={isRtl ? 'right' : 'left'}
                            width={150}
                            tick={{
                                fill: theme.palette.text.primary,
                                fontSize: 12,
                                textAnchor: "end",
                            }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <ChartTooltip
                            content={renderTooltipContent}
                            cursor={false}
                        />
                        <Bar
                            dataKey="value"
                            barSize={12}
                            shape={<CustomBarShape />}
                        >
                            <LabelList
                                dataKey="percentageLabel"
                                content={({ y, height, value }: ILabelListContentProps) => (
                                    <text
                                        x={isRtl ? '0%' : '100%'}
                                        y={Number(y ?? 0) + Number(height ?? 0) / 2}
                                        textAnchor="end"
                                        dominantBaseline="central"
                                        fill={theme.palette.text.primary}
                                        fontSize={12}
                                    >
                                        {value}
                                    </text>
                                )}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </StyledConversionCard>
    );
};

export default ConversionBarChart;
